import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Upload, Image as ImageIcon, X, Search, Check, ChevronDown, RefreshCw, FolderSearch } from 'lucide-react';
import { authed } from '../lib/api';

/**
 * <ImagePickerButton> — Batch B2 Bug-Fix Pass 3
 *
 * A single control that presents the admin with two ways to attach an image:
 *   1. Upload from Device — same behaviour as before (multipart POST /api/admin/uploads).
 *   2. Pick from Cloudinary — opens an in-app modal listing existing images
 *      in the Cloudinary account (via /api/admin/cloudinary/list) and lets
 *      the admin select one; the selected image's `secure_url` becomes the
 *      new value.
 *
 * Props:
 *  - realm            : 'admin' | 'super_admin' — auth realm to use for API calls.
 *  - subdir           : subfolder for uploads (e.g. 'flavours', 'heroes', 'variants').
 *  - label            : button label (default: 'Image').
 *  - size             : 'sm' | 'md' — button size.
 *  - variant          : 'button' | 'outline' — visual variant.
 *  - onPicked(url)    : called when the admin selects/uploads an image (URL string).
 *  - accept           : file input accept, default 'image/*'.
 *  - testIdPrefix     : optional data-testid prefix so tests can target the picker.
 */
export const ImagePickerButton = ({
    realm = 'super_admin',
    subdir = 'general',
    label = 'Image',
    size = 'sm',
    variant = 'outline',
    onPicked,
    accept = 'image/*',
    testIdPrefix,
}) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const menuRef = useRef(null);

    // Close the mini menu on any outside click.
    useEffect(() => {
        const onDown = (e) => {
            if (!menuRef.current) return;
            if (!menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        if (menuOpen) document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [menuOpen]);

    const uploadFromDevice = () => {
        setMenuOpen(false);
        fileRef.current?.click();
    };

    const onDeviceFileChosen = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', f);
            const r = await authed(realm, {
                method: 'POST',
                url: `/admin/uploads?subdir=${encodeURIComponent(subdir)}`,
                data: fd,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const url = r.data?.url || '';
            if (url) onPicked?.(url);
        } finally {
            setUploading(false);
            // Reset input so the same file can be re-picked
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const openCloudinaryPicker = () => {
        setMenuOpen(false);
        setPickerOpen(true);
    };

    const btnCls = variant === 'button'
        ? 'inline-flex items-center gap-1.5 rounded-full bg-sdf-red text-white font-bold px-3 py-1.5 text-xs hover:bg-[#A81616] disabled:opacity-60'
        : 'inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-800 hover:bg-neutral-50 disabled:opacity-60';
    const iconSize = size === 'md' ? 14 : 12;

    return (
        <div className="relative inline-block" ref={menuRef}>
            <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                disabled={uploading}
                className={btnCls}
                data-testid={testIdPrefix ? `${testIdPrefix}-open` : 'image-picker-open'}
            >
                <ImageIcon size={iconSize} />
                <span>{uploading ? 'Uploading…' : label}</span>
                <ChevronDown size={iconSize} className="opacity-70" />
            </button>

            {menuOpen && (
                <div
                    className="absolute z-50 mt-1 left-0 min-w-[220px] bg-white border border-sdf-line rounded-xl shadow-lg overflow-hidden"
                    role="menu"
                    data-testid={testIdPrefix ? `${testIdPrefix}-menu` : 'image-picker-menu'}
                >
                    <button
                        type="button"
                        onClick={uploadFromDevice}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-sdf-cream/40 border-b border-sdf-line/60"
                        data-testid={testIdPrefix ? `${testIdPrefix}-upload-device` : 'image-picker-upload'}
                    >
                        <Upload size={14} className="text-sdf-red" />
                        <div className="flex-1">
                            <div className="font-bold text-sdf-text">Upload from Device</div>
                            <div className="text-[10px] text-sdf-text-2">Pick a file from your phone or computer</div>
                        </div>
                    </button>
                    <button
                        type="button"
                        onClick={openCloudinaryPicker}
                        className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-sdf-cream/40"
                        data-testid={testIdPrefix ? `${testIdPrefix}-pick-cloudinary` : 'image-picker-cloudinary'}
                    >
                        <FolderSearch size={14} className="text-sdf-red" />
                        <div className="flex-1">
                            <div className="font-bold text-sdf-text">Pick from Cloudinary</div>
                            <div className="text-[10px] text-sdf-text-2">Browse images already uploaded</div>
                        </div>
                    </button>
                </div>
            )}

            <input
                ref={fileRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={onDeviceFileChosen}
                data-testid={testIdPrefix ? `${testIdPrefix}-file-input` : 'image-picker-file'}
            />

            {pickerOpen && (
                <CloudinaryPickerModal
                    realm={realm}
                    onClose={() => setPickerOpen(false)}
                    onPick={(url) => { onPicked?.(url); setPickerOpen(false); }}
                    testIdPrefix={testIdPrefix}
                />
            )}
        </div>
    );
};


// ----------------------------------------------------------------------------
// Cloudinary picker modal
// ----------------------------------------------------------------------------

const CloudinaryPickerModal = ({ realm, onClose, onPick, testIdPrefix }) => {
    const [loading, setLoading] = useState(true);
    const [available, setAvailable] = useState(true);
    const [reason, setReason] = useState('');
    const [resources, setResources] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [folder, setFolder] = useState('Sri Durga Foods(Distributors)');
    const [subfolders, setSubfolders] = useState([]);
    const [query, setQuery] = useState('');

    const load = async (reset = true) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ folder, max_results: '200' });
            if (!reset && nextCursor) params.set('next_cursor', nextCursor);
            const r = await authed(realm, { method: 'GET', url: `/admin/cloudinary/list?${params.toString()}` });
            const data = r.data || {};
            setAvailable(!!data.available);
            setReason(data.reason || '');
            const items = Array.isArray(data.resources) ? data.resources : [];
            setResources(reset ? items : [...resources, ...items]);
            setNextCursor(data.next_cursor || null);
        } catch (e) {
            setAvailable(false);
            setReason(e?.response?.data?.detail || 'Failed to load Cloudinary resources.');
        } finally {
            setLoading(false);
        }
    };

    const loadFolders = async () => {
        try {
            const r = await authed(realm, { method: 'GET', url: '/admin/cloudinary/folders' });
            setSubfolders(Array.isArray(r.data?.folders) ? r.data.folders : []);
        } catch { /* silent */ }
    };

    useEffect(() => { load(true); }, [folder]);       // eslint-disable-line
    useEffect(() => { loadFolders(); }, []);          // eslint-disable-line

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return resources;
        return resources.filter(r => (r.public_id || '').toLowerCase().includes(q));
    }, [query, resources]);

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center px-3 py-6"
            role="dialog"
            aria-modal="true"
            data-testid={testIdPrefix ? `${testIdPrefix}-cloudinary-modal` : 'cloudinary-modal'}
        >
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative w-full max-w-5xl max-h-[90vh] overflow-hidden bg-sdf-ivory rounded-2xl shadow-2xl border border-sdf-line flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-sdf-line bg-white/70 backdrop-blur">
                    <div className="flex items-center gap-2">
                        <FolderSearch size={16} className="text-sdf-red" />
                        <div className="text-sm font-extrabold tracking-widest uppercase text-sdf-text">Pick from Cloudinary</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => load(true)}
                            className="p-1.5 rounded-full hover:bg-black/5"
                            title="Refresh"
                            data-testid="cloudinary-refresh"
                        >
                            <RefreshCw size={14} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-full hover:bg-black/5"
                            aria-label="Close"
                            data-testid="cloudinary-close"
                        >
                            <X size={16} />
                        </button>
                    </div>
                </div>

                {/* Search & folder switcher */}
                <div className="px-4 py-3 border-b border-sdf-line bg-white/50 flex flex-wrap items-center gap-2">
                    <div className="flex-1 min-w-[180px] flex items-center gap-2 bg-white border border-sdf-line rounded-xl px-3 py-2">
                        <Search size={14} className="text-neutral-500" />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Filter by name…"
                            className="flex-1 outline-none bg-transparent text-sm py-0.5"
                            data-testid="cloudinary-search"
                        />
                    </div>
                    <select
                        value={folder}
                        onChange={(e) => { setFolder(e.target.value); setNextCursor(null); }}
                        className="border border-sdf-line rounded-xl bg-white px-2 py-2 text-sm"
                        data-testid="cloudinary-folder-select"
                    >
                        <option value="Sri Durga Foods(Distributors)">All (root)</option>
                        {subfolders.map(sf => (
                            <option key={sf} value={sf}>{sf.replace('Sri Durga Foods(Distributors)/', '')}</option>
                        ))}
                    </select>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-3">
                    {!available && (
                        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800" data-testid="cloudinary-unavailable">
                            <div className="font-bold">Cloudinary is not available right now.</div>
                            <div className="text-xs mt-1">{reason || 'Please try again in a moment or upload from your device instead.'}</div>
                        </div>
                    )}

                    {available && loading && resources.length === 0 && (
                        <div className="py-12 text-center text-sm text-sdf-text-2">Loading Cloudinary library…</div>
                    )}

                    {available && !loading && filtered.length === 0 && (
                        <div className="py-12 text-center text-sm text-sdf-text-2" data-testid="cloudinary-empty">No images found in this folder.</div>
                    )}

                    {available && filtered.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="cloudinary-grid">
                            {filtered.map(r => (
                                <button
                                    key={r.public_id}
                                    onClick={() => onPick?.(r.url)}
                                    className="group relative rounded-xl overflow-hidden border border-sdf-line bg-white hover:border-sdf-red transition-colors"
                                    data-testid={`cloudinary-pick-${(r.public_id || '').split('/').pop()}`}
                                    title={r.public_id}
                                >
                                    <div className="aspect-square w-full bg-neutral-100 flex items-center justify-center">
                                        <img
                                            src={r.url}
                                            alt={r.public_id}
                                            loading="lazy"
                                            className="max-w-full max-h-full object-contain"
                                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        />
                                    </div>
                                    <div className="p-2 border-t border-sdf-line bg-white/70">
                                        <div className="text-[10px] text-sdf-text-2 line-clamp-1">{(r.public_id || '').split('/').pop()}</div>
                                        <div className="text-[10px] text-sdf-text-2/70">{r.width}×{r.height}</div>
                                    </div>
                                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/25 flex items-center justify-center">
                                        <div className="rounded-full bg-white text-sdf-red p-2 shadow-md">
                                            <Check size={16} />
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer with pagination */}
                <div className="px-4 py-3 border-t border-sdf-line bg-white/60 flex items-center justify-between text-xs">
                    <div className="text-sdf-text-2">
                        {available ? `${resources.length} image${resources.length === 1 ? '' : 's'} loaded` : 'Cloudinary not connected'}
                    </div>
                    <div className="flex items-center gap-2">
                        {nextCursor && (
                            <button
                                onClick={() => load(false)}
                                className="btn-outline text-xs"
                                disabled={loading}
                                data-testid="cloudinary-load-more"
                            >
                                {loading ? 'Loading…' : 'Load more'}
                            </button>
                        )}
                        <button onClick={onClose} className="btn-outline text-xs" data-testid="cloudinary-cancel">Cancel</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImagePickerButton;
